'use client';

import type { TooltipProviderProps } from '@radix-ui/react-tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { TooltipProvider } from '../molecules/tooltip/tooltip';
import { RandomChildProvider } from './random-child';
import type { RouterContextProps } from './router';
import { RouterProvider } from './router';
import { TranslationsProvider } from './translations';

/**
 * Ark Provider!
 */
export type ArkProviderProps = React.PropsWithChildren & {
  /**
   * Options to override default functionality for tooltips.
   */
  tooltips?: TooltipProviderProps;
  /**
   * Theoretical prop in case we need a router other than Next's.
   */
  routerData?: RouterContextProps;
  locale?: string;
  loadRandomChild?: boolean;
};
export const ArkProvider: React.FC<ArkProviderProps> = ({
  children,
  routerData,
  tooltips,
  locale,
  loadRandomChild
}) => {
  // This is assuming we're always within NextJS.
  // In reality we doubt we'll ever be using this outside of NextJS apps,
  // but we do then we should come up with a slicker way to lazy load the NextJS functionality.
  const nextRouter = useRouter();
  const queryClient = new QueryClient();
  const routerProps = {
    routerData: {
      router: nextRouter,
      RouterComponent: Link,
      // eslint-disable-next-line react-compiler/react-compiler -- usePathname is a framework-agnostic hook passed via RouterContext
      usePathname,
      ...routerData
    }
  };
  return <TranslationsProvider locale={locale || 'en'}>
      <RouterProvider {...routerProps}>
        <NuqsAdapter processUrlSearchParams={search => {
        search.sort();
        return search;
      }}>
          <TooltipProvider {...tooltips}>
            <QueryClientProvider client={queryClient}>
              <RandomChildProvider loadRandomChild={loadRandomChild}>
                {children}
              </RandomChildProvider>
            </QueryClientProvider>
          </TooltipProvider>
        </NuqsAdapter>
      </RouterProvider>
    </TranslationsProvider>;
};